// src/pages/admin/AdminSchedulesPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import 'dayjs/locale/zh-cn';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  fetchAdminScheduleMatrix, // (start: string, days: number) —— 週ごとのカレンダーマトリクス
  fetchDayCaps,             // (start: string, days: number) —— 日ごとの並行上限取得
  setDayCap,                // (date: string, value: number) —— 日ごとの並行上限更新
  fetchDoctors,             // () —— 医師一覧取得
} from '../../api/adminSchedules';
import api from '../../api/axios';

// ---------- 型定義 ----------
type AdminCalendarReservation = {
  id: number;
  user_id: number;
  status: string;
  // バックエンドのフィールド名差異に対応するため、両方を許容
  patient_name?: string;
  user_name?: string;
  doctor_id?: number | null;
};

type AdminCalendarSlot = {
  time: string;                   // "HH:MM" または "H:MM"
  used: number;                   // この時間帯に出勤している医師数
  max?: number;                   // その日の並行上限（未指定の場合 caps[date] を利用）
  scheduled_doctor_ids: number[];
  reservation_count?: number | string; // この時間帯の予約件数（数値 or 文字列を許容）
  // 詳細モードの場合のみ返ってくる情報
  bookable?: boolean;
  reservations?: AdminCalendarReservation[];
};

type AdminCalendarDay = {
  date: string;                   // "YYYY-MM-DD"
  slots: AdminCalendarSlot[];
};

type Doctor = {
  id: number;
  display_name?: string | null;
  full_name?: string | null;
  name?: string | null;
  email?: string | null;
} & Record<string, any>;

// ---------- ヘルパー ----------
const generateTimeSlots = () => {
  const morning = [9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5];
  const afternoon = [13, 13.5, 14, 14.5, 15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5];
  const fmt = (n: number) =>
    `${String(Math.floor(n)).padStart(2, '0')}:${n % 1 === 0 ? '00' : '30'}`;
  return [...morning, ...afternoon].map(fmt);
};

// "H:MM" → "HH:MM" に統一
const toHHMM = (s: string) => {
  const [h, m] = String(s).split(':');
  return `${String(Number(h)).padStart(2, '0')}:${m}`;
};

// その枠が現在より過去かどうか
const isPastSlot = (dateStr: string, timeStr: string) => {
  const [h, m] = toHHMM(timeStr).split(':').map(Number);
  const dt = dayjs(dateStr).hour(h).minute(m).second(0);
  return dt.isBefore(dayjs());
};

// カラードットの判定
// 灰: 過去枠
// 赤: 医師は出勤していて、予約で実効キャパシティがすべて埋まっている
// 黄: 医師は出勤しているが、まだ予約 0 件
// 緑: それ以外（空きあり）
const getDotClass = (
  date: string,
  time: string,
  used: number,
  max: number,
  reservationCount?: number | string
) => {
  if (isPastSlot(date, time)) return 'bg-gray-300';

  // バックエンドが "1" のような文字列を返しても確実に数値化する
  const reserved = Number(reservationCount ?? 0);

  // 実効並行上限 = min(出勤医師数, 当日並行上限)
  const effectiveCap = Math.min(used, max);

  // 空き枠数
  const available = Math.max(0, effectiveCap - reserved);

  // 医師がいない、またはそもそも利用不可 → 赤（運用上の異常）
  if (effectiveCap <= 0) {
    return 'bg-red-500';
  }

  // 予約で実効キャパシティがすべて埋まっている
  if (reserved > 0 && available === 0) {
    return 'bg-red-500';
  }

  // 医師はいるが予約 0 件 → これから埋めたい枠（黄色）
  if (used > 0 && reserved === 0) {
    return 'bg-yellow-400';
  }

  // 上記以外（予約はあるがまだ空きもある）→ 緑
  return 'bg-green-400';
};

const AdminSchedulesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // 週オフセットと、週ごとのカレンダーマトリクス
  const [weekOffset, setWeekOffset] = useState(0);
  const [matrix, setMatrix] = useState<AdminCalendarDay[]>([]);
  const [loading, setLoading] = useState(false);

  // 医師一覧
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const docName = useMemo(() => {
    const map = new Map<number, string>();
    doctors.forEach((d) => {
      const best =
        (d.display_name && String(d.display_name).trim()) ||
        (d.full_name && String(d.full_name).trim()) ||
        (d.name && String(d.name).trim()) ||
        (d.email && String(d.email).trim()) ||
        `Doctor#${d.id}`;
      map.set(d.id, best);
    });
    return map;
  }, [doctors]);

  // 当該週の各日の並行上限（バックエンドから取得／上書き）
  const [caps, setCaps] = useState<Record<string, number>>({});
  // 画面右上の「デフォルト並行上限」（表示用 & 今週へ一括適用）
  const [defaultCap, setDefaultCap] = useState<number>(3);

  // テーブル上部の used/max 行内編集用の状態
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<number>(3);

  // モーダル：枠の詳細（医師リスト / 予約一覧 / 当日上限編集）
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailDate, setDetailDate] = useState<string>('');
  const [detailTime, setDetailTime] = useState<string>('');
  const [detailSlot, setDetailSlot] = useState<AdminCalendarSlot | null>(null);
  const [detailDayCap, setDetailDayCap] = useState<number>(3);

  const start = useMemo(
    () => dayjs().startOf('day').add(weekOffset * 7, 'day'),
    [weekOffset]
  );
  const times = useMemo(() => generateTimeSlots(), []);

  // dayjs のロケール設定
  useEffect(() => {
    dayjs.locale(i18n.language === 'zh' ? 'zh-cn' : 'ja');
  }, [i18n.language]);

  // 医師一覧取得
  useEffect(() => {
    (async () => {
      try {
        const list = await fetchDoctors();
        setDoctors(list || []);
      } catch {
        toast.error(
          t('admin.schedules.fetch_doctors_failed') || '医師一覧の取得に失敗しました'
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // カレンダーマトリクスと当日上限をまとめて取得
  const load = async () => {
    setLoading(true);
    try {
      const startStr = start.format('YYYY-MM-DD');
      const [mx, capResp] = await Promise.all([
        fetchAdminScheduleMatrix(startStr, 7), // 内部で details=0/1 を設定
        fetchDayCaps(startStr, 7),
      ]);
      // スロットの time を "HH:MM" 形式に正規化
      const fixed = (mx || []).map((d: AdminCalendarDay) => ({
        ...d,
        slots: (d.slots || []).map((s) => ({ ...s, time: toHHMM(s.time) })),
      }));
      setMatrix(fixed);
      setCaps(capResp || {});
    } catch (e) {
      console.error(e);
      toast.error(t('admin.schedules.fetch_failed') || 'スケジュールの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset]);

  const formatWeekday = (dateStr: string) => {
    const di = dayjs(dateStr).day();
    const zhWeek = ['日', '一', '二', '三', '四', '五', '六'];
    const jaWeek = ['日', '月', '火', '水', '木', '金', '土'];
    return i18n.language === 'zh' ? `周${zhWeek[di]}` : jaWeek[di];
  };

  // used/max の表示クリックで、当日上限のインライン編集を開始
  const openCellEditor = (date: string, time: string, currentMax: number) => {
    setEditingKey(`${date}|${time}`);
    setEditingValue(currentMax);
  };
  const cancelCellEditor = () => setEditingKey(null);
  const saveCellEditor = async (date: string) => {
    try {
      const val = Math.max(1, editingValue);
      await setDayCap(date, val); // 当日上限の更新
      setCaps((prev) => ({ ...prev, [date]: val }));
      toast.success(t('admin.schedules.cap_saved') || '上限を更新しました');
      setEditingKey(null);
      await load();
    } catch (e) {
      console.error(e);
      toast.error(t('admin.schedules.cap_save_failed') || '上限の更新に失敗しました');
    }
  };

  // デフォルト並行上限を当週の全日に一括適用
  const applyDefaultToWeek = async () => {
    try {
      const dates = matrix.map((d) => d.date);
      await Promise.allSettled(dates.map((d) => setDayCap(d, defaultCap)));
      const merged: Record<string, number> = {};
      dates.forEach((d) => (merged[d] = defaultCap));
      setCaps((prev) => ({ ...prev, ...merged }));
      toast.success(t('admin.schedules.week_applied') || '今週の並行上限を一括適用しました');
      await load();
    } catch (e) {
      console.error(e);
      toast.error(t('admin.schedules.week_apply_failed') || '一括適用に失敗しました');
    }
  };

  // 単一日の詳細（reservations 付き）だけ取得
  const fetchDayDetail = async (date: string) => {
    const { data } = await api.get<AdminCalendarDay[]>(
      '/admin/schedules/calendar',
      { params: { start: date, days: 1, details: 1 } } // バックエンドのパラメータ名に合わせる
    );
    return (data && data[0]) || null;
  };

  // 詳細モーダルを開く（当日以降のみクリック可）
  const openDetail = async (date: string, time: string) => {
    if (isPastSlot(date, time)) return;

    setDetailOpen(true);
    setDetailLoading(true);
    setDetailDate(date);
    setDetailTime(toHHMM(time));
    setDetailSlot(null);
    setDetailDayCap(caps[date] ?? 3);

    try {
      const day = await fetchDayDetail(date);
      if (!day) {
        toast.error(t('admin.schedules.fetch_failed') || 'スケジュールの取得に失敗しました');
        setDetailLoading(false);
        return;
      }
      const slot = (day.slots || []).find((s) => toHHMM(s.time) === toHHMM(time));
      setDetailSlot(slot || null);
      setDetailDayCap(caps[date] ?? (slot?.max as number | undefined) ?? 3);
    } catch (e) {
      console.error(e);
      toast.error(t('admin.schedules.fetch_failed') || 'スケジュールの取得に失敗しました');
    } finally {
      setDetailLoading(false);
    }
  };

  // 詳細モーダル内の「当日上限」を保存
  const saveDetailDayCap = async () => {
    try {
      const val = Math.max(1, detailDayCap);
      await setDayCap(detailDate, val);
      setCaps((prev) => ({ ...prev, [detailDate]: val }));
      toast.success(t('admin.schedules.cap_saved') || '上限を更新しました');
      await load();
      setDetailOpen(false);
    } catch (e) {
      console.error(e);
      toast.error(t('admin.schedules.cap_save_failed') || '上限の更新に失敗しました');
    }
  };

  // —— UI レンダリング —— //
  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* 上部：タイトル + デフォルト並行上限 + 週移動 + 医師別ビュー */}
        <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">
              {t('admin.schedules.title') || '管理：医師スケジュール'}
            </h1>
            <p className="text-gray-500 mt-1 text-sm">
              {t('admin.schedules.subtitle') ||
                '（全体表示：各枠の出勤医師を一覧表示。マスをクリックして当日の上限と予約詳細を確認できます）'}
            </p>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-3">
            {/* デフォルト上限 / 今週へ一括適用 / 医師別ビュー */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 whitespace-nowrap">
                {t('admin.schedules.default_cap') || '並行上限（デフォルト）'}
              </label>
              <input
                type="number"
                min={1}
                max={20}
                value={defaultCap}
                onChange={(e) => setDefaultCap(Math.max(1, Number(e.target.value)))}
                className="w-20 border rounded px-2 py-1 bg-white"
              />
              <button
                className="min-w-[160px] px-4 py-2 rounded bg-coral text-white text-sm md:text-base font-medium hover:opacity-90 whitespace-nowrap"
                onClick={applyDefaultToWeek}
              >
                {t('admin.schedules.apply_week') || '今週に一括適用'}
              </button>
              <button
                className="min-w-[160px] px-4 py-2 rounded bg-indigo-600 text-white text-sm md:text-base font-medium hover:bg-indigo-700 whitespace-nowrap"
                onClick={() => navigate('/admin/schedules/doctor')}
              >
                {t('admin.schedules.doctor_view') || '医師別ビュー'}
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                className="px-4 py-2 border rounded hover:bg-gray-100 whitespace-nowrap"
                onClick={() => setWeekOffset((p) => Math.max(0, p - 1))}
                disabled={weekOffset === 0}
              >
                {t('admin.schedules.prev_week') || '← 前の週'}
              </button>
              <button
                className="px-4 py-2 border rounded hover:bg-gray-100 whitespace-nowrap"
                onClick={() => setWeekOffset((p) => p + 1)}
              >
                {t('admin.schedules.next_week') || '次の週 →'}
              </button>
            </div>
          </div>
        </div>

        {/* カレンダー表（白背景） */}
        <div className="overflow-x-auto border rounded-lg bg-white">
          <table className="min-w-full border-collapse text-center">
            <thead>
              <tr>
                <th className="bg-gray-100 px-3 py-3 border sticky left-0 z-10">
                  {t('admin.schedules.time') || '時間'}
                </th>
                {matrix.map((d) => (
                  <th key={d.date} className="bg-gray-100 px-3 py-3 border whitespace-nowrap">
                    {dayjs(d.date).format('MM/DD')}（{formatWeekday(d.date)}）
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={1 + matrix.length} className="py-10 text-gray-500">
                    {t('admin.common.loading') || '読み込み中…'}
                  </td>
                </tr>
              ) : (
                times.map((tm) => (
                  <tr key={tm}>
                    <td className="bg-gray-50 border px-3 py-2 sticky left-0 z-0 font-semibold">
                      {tm}
                    </td>
                    {matrix.map((day) => {
                      const slot = day.slots.find((s) => toHHMM(s.time) === toHHMM(tm));
                      const used = slot?.used ?? 0; // 出勤医師数
                      const dayCap = caps[day.date] ?? 3;
                      const max = slot?.max ?? dayCap;
                      const ids = slot?.scheduled_doctor_ids || [];
                      // ここで必ず数値に変換する（"1" -> 1）
                      const reservedCnt = Number(slot?.reservation_count ?? 0);

                      const key = `${day.date}|${tm}`;
                      const isEditing = editingKey === key;
                      const disabled = isPastSlot(day.date, tm);

                      return (
                        <td key={key} className="border px-2 py-2 align-top">
                          {/* 上段：並行上限表示 + クリックで当日上限編集 */}
                          {!isEditing ? (
                            <div
                              className={classNames(
                                'flex items-center justify-center gap-2 mb-2',
                                disabled ? 'opacity-50' : 'cursor-pointer group'
                              )}
                              onClick={() => !disabled && openCellEditor(day.date, tm, max)}
                            >
                              <span className="text-xs text-gray-700 font-medium group-hover:underline">
                                {used} / {max}
                              </span>
                              <span
                                className={classNames(
                                  'inline-block w-3 h-3 rounded-full',
                                  getDotClass(day.date, tm, used, max, reservedCnt)
                                )}
                              />
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-1 mb-2">
                              <input
                                type="number"
                                min={1}
                                max={20}
                                value={editingValue}
                                onChange={(e) =>
                                  setEditingValue(Math.max(1, Number(e.target.value)))
                                }
                                className="w-16 border rounded px-2 py-1 text-sm"
                                autoFocus
                              />
                              <button
                                className="px-2 py-1 text-xs rounded bg-coral text-white hover:opacity-90"
                                onClick={() => saveCellEditor(day.date)}
                              >
                                {t('admin.common.save') || '保存'}
                              </button>
                              <button
                                className="px-2 py-1 text-xs rounded border hover:bg-gray-50"
                                onClick={cancelCellEditor}
                              >
                                {t('admin.common.cancel') || 'キャンセル'}
                              </button>
                            </div>
                          )}

                          {/* 下段：医師チップ一覧。クリックで詳細モーダルを開く（未来枠のみ） */}
                          <div
                            className={classNames(
                              'min-h-[24px]',
                              disabled ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                            )}
                            onClick={() => !disabled && openDetail(day.date, tm)}
                          >
                            {ids.length > 0 ? (
                              <div className="flex flex-wrap gap-1 justify-center">
                                {ids.map((id) => (
                                  <span
                                    key={id}
                                    className="px-2 py-0.5 text-xs rounded-full bg-coral/5 text-coral border border-coral/20"
                                  >
                                    {docName.get(id) || `Doctor#${id}`}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <div className="text-xs text-gray-400">
                                {t('admin.schedules.no_doctor') || '（未設定）'}
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 詳細モーダル（当日上限 + 医師一覧 + 予約一覧） */}
      {detailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* オーバーレイ */}
          <div className="absolute inset-0 bg-black/40" onClick={() => setDetailOpen(false)} />
          {/* モーダル本体 */}
          <div className="relative bg-white w-full max-w-2xl mx-4 rounded-2xl shadow-xl p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-lg font-semibold">
                  {dayjs(detailDate).format('YYYY/MM/DD')}（
                  {(() => {
                    const di = dayjs(detailDate).day();
                    const zhWeek = ['日', '一', '二', '三', '四', '五', '六'];
                    const jaWeek = ['日', '月', '火', '水', '木', '金', '土'];
                    return i18n.language === 'zh' ? `周${zhWeek[di]}` : jaWeek[di];
                  })()}
                  ） {detailTime}
                </div>
                {!detailLoading && detailSlot && (
                  <div className="text-sm text-gray-600 mt-1">
                    {/* 当日上限 / 実効並行上限 / 予約数 / 空き枠をまとめて表示 */}
                    {(() => {
                      const cap = caps[detailDate] ?? (detailSlot.max as number | undefined) ?? 3;
                      const reserved = Number(detailSlot.reservation_count ?? 0);
                      const effectiveCap = Math.min(detailSlot.used, cap);
                      const available = Math.max(0, effectiveCap - reserved);
                      return (
                        <>
                          {t('admin.schedules.scheduled') || '出勤医師数'}: {detailSlot.used} ・{' '}
                          {t('admin.schedules.capacity') || '並行上限'}: {cap} ・{' '}
                          {t('admin.schedules.reserved_count') || '予約数'}: {reserved} ・{' '}
                          {t('admin.schedules.available') || '空き枠'}: {available}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
              <button
                className="px-3 py-1 rounded border hover:bg-gray-50"
                onClick={() => setDetailOpen(false)}
              >
                ✕
              </button>
            </div>

            {/* 当日上限の編集フォーム */}
            <div className="mb-4">
              <label className="text-sm text-gray-700 mr-2">
                {t('admin.schedules.edit_day_cap') || '当日の並行上限'}
              </label>
              <input
                type="number"
                min={1}
                max={20}
                value={detailDayCap}
                onChange={(e) => setDetailDayCap(Math.max(1, Number(e.target.value)))}
                className="w-24 border rounded px-2 py-1"
              />
              <button
                className="ml-2 px-3 py-1 rounded bg-coral text-white hover:opacity-90"
                onClick={saveDetailDayCap}
                disabled={detailLoading}
              >
                {t('admin.common.save') || '保存'}
              </button>
            </div>

            {/* モーダルのコンテンツ本体 */}
            {detailLoading ? (
              <div className="text-gray-500 py-6 text-center">
                {t('admin.common.loading') || '読み込み中…'}
              </div>
            ) : detailSlot ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 医師一覧 */}
                <div className="border rounded-lg p-3">
                  <div className="font-medium mb-2">
                    {t('admin.schedules.scheduled_doctors') || '出勤医師'}
                  </div>
                  {detailSlot.scheduled_doctor_ids?.length ? (
                    <ul className="space-y-1">
                      {detailSlot.scheduled_doctor_ids.map((id) => (
                        <li key={id} className="text-sm">
                          • {docName.get(id) || `Doctor#${id}`}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-sm text-gray-500">
                      {t('admin.schedules.no_doctor') || '（未設定）'}
                    </div>
                  )}
                  {/* 空き枠 = 実効並行上限 - 予約数（最低 0） */}
                  <div className="mt-3 text-xs text-gray-500">
                    {(() => {
                      const cap = caps[detailDate] ?? (detailSlot.max as number | undefined) ?? 3;
                      const reserved = Number(detailSlot.reservation_count ?? 0);
                      const effectiveCap = Math.min(detailSlot.used, cap);
                      const free = Math.max(0, effectiveCap - reserved);
                      return (
                        <>
                          {t('admin.schedules.free_doctors') || '利用可能な空き枠'}: {free}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* 予約一覧 */}
                <div className="border rounded-lg p-3">
                  <div className="font-medium mb-2">
                    {t('admin.schedules.reservations') || '予約一覧'}
                  </div>
                  {detailSlot.reservations && detailSlot.reservations.length > 0 ? (
                    <ul className="space-y-2 max-h-56 overflow-auto pr-1">
                      {detailSlot.reservations.map((r) => {
                        const displayName = r.patient_name || r.user_name || `#${r.user_id}`;
                        return (
                          <li
                            key={r.id}
                            className="text-sm border rounded px-2 py-1 flex items-center justify-between"
                          >
                            <span>
                              {displayName}
                              <span className="ml-2 text-xs text-gray-500">#{r.user_id}</span>
                            </span>
                            <span
                              className={classNames(
                                'text-xs rounded px-1.5 py-0.5',
                                r.status === 'reserved'
                                  ? 'bg-green-50 text-green-700 border border-green-200'
                                  : 'bg-gray-50 text-gray-700 border border-gray-200'
                              )}
                            >
                              {r.status}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <div className="text-sm text-gray-500">
                      {t('admin.schedules.no_reservation') || '予約はありません'}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-gray-500 py-6 text-center">
                {t('admin.schedules.slot_not_found') || '該当する時間枠が見つかりません'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSchedulesPage;
